/**
 * KPICard Component
 *
 * Componente para exibir um KPI com ícone, valor, label e mudança.
 * Suporta tema claro e escuro.
 */

import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Landmark, CreditCard, Banknote } from 'lucide-react'
import './KPICard.css'

const ACCOUNT_TYPE_LABELS = {
  digital: 'Digital',
  physical: 'Física'
}

const VIEWPORT_MARGIN = 12
const MENU_GAP = 10

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0)
}

function formatDueDate(value) {
  if (!value) return 'N/A'
  const [year, month, day] = String(value).split('T')[0].split('-')
  if (!year || !month || !day) return value
  return `${day}/${month}/${year}`
}

function computeFlyoutPosition(anchorRect, menuWidth, menuHeight) {
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const width = Math.min(menuWidth, viewportWidth - VIEWPORT_MARGIN * 2)
  const height = Math.min(menuHeight, viewportHeight - VIEWPORT_MARGIN * 2)

  let left = anchorRect.left
  if (left + width > viewportWidth - VIEWPORT_MARGIN) {
    left = anchorRect.right - width
  }
  left = Math.max(VIEWPORT_MARGIN, Math.min(left, viewportWidth - width - VIEWPORT_MARGIN))

  const spaceBelow = viewportHeight - anchorRect.bottom - VIEWPORT_MARGIN
  const spaceAbove = anchorRect.top - VIEWPORT_MARGIN

  let top
  if (spaceBelow >= height + MENU_GAP || spaceBelow >= spaceAbove) {
    top = anchorRect.bottom + MENU_GAP
  } else {
    top = anchorRect.top - height - MENU_GAP
  }
  top = Math.max(VIEWPORT_MARGIN, Math.min(top, viewportHeight - height - VIEWPORT_MARGIN))

  return {
    top,
    left,
    width,
    maxHeight: height
  }
}

function KPICard({
  label,
  value,
  change,
  up,
  icon: Icon,
  color,
  bg,
  accountBalances = null,
  details = null,
  actionPath = '/erp/financeiro/extrato-caixa',
  onClick,
  tourAnchor
}) {
  const navigate = useNavigate()
  const [accountsOpen, setAccountsOpen] = useState(false)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [menuStyle, setMenuStyle] = useState(null)
  const [detailsMenuStyle, setDetailsMenuStyle] = useState(null)
  const triggerRef = useRef(null)
  const cardRef = useRef(null)
  const menuRef = useRef(null)
  const detailsMenuRef = useRef(null)
  const showAccountsFlyout = accountBalances?.accounts?.length > 0
  const detailItems = details?.items || []
  const showDetailsFlyout = Boolean(details)

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const menuHeight = menuRef.current?.offsetHeight || 280
    setMenuStyle(computeFlyoutPosition(rect, 352, menuHeight))
  }, [])

  const updateDetailsMenuPosition = useCallback(() => {
    if (!cardRef.current) return
    const rect = cardRef.current.getBoundingClientRect()
    const menuHeight = detailsMenuRef.current?.offsetHeight || 360
    setDetailsMenuStyle(computeFlyoutPosition(rect, 520, menuHeight))
  }, [])

  useEffect(() => {
    if (!accountsOpen) return undefined

    updateMenuPosition()
    window.addEventListener('resize', updateMenuPosition)
    window.addEventListener('scroll', updateMenuPosition, true)

    const handleClickOutside = (event) => {
      if (
        triggerRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return
      }
      setAccountsOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      window.removeEventListener('resize', updateMenuPosition)
      window.removeEventListener('scroll', updateMenuPosition, true)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [accountsOpen, updateMenuPosition])

  useEffect(() => {
    if (!detailsOpen) return undefined

    updateDetailsMenuPosition()
    window.addEventListener('resize', updateDetailsMenuPosition)
    window.addEventListener('scroll', updateDetailsMenuPosition, true)

    const handleClickOutside = (event) => {
      if (
        cardRef.current?.contains(event.target) ||
        detailsMenuRef.current?.contains(event.target)
      ) {
        return
      }
      setDetailsOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      window.removeEventListener('resize', updateDetailsMenuPosition)
      window.removeEventListener('scroll', updateDetailsMenuPosition, true)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [detailsOpen, updateDetailsMenuPosition])

  useLayoutEffect(() => {
    if (accountsOpen) updateMenuPosition()
  }, [accountsOpen, updateMenuPosition, accountBalances?.accounts?.length])

  useLayoutEffect(() => {
    if (detailsOpen) updateDetailsMenuPosition()
  }, [detailsOpen, updateDetailsMenuPosition, detailItems.length])

  const cardClassName = [
    'kpi-card',
    showAccountsFlyout ? 'kpi-card--has-accounts-flyout' : '',
    showDetailsFlyout || onClick ? 'kpi-card--clickable' : '',
    accountsOpen || detailsOpen ? 'kpi-card--accounts-open' : ''
  ].filter(Boolean).join(' ')

  const accountsMenu = accountsOpen && menuStyle && (
    <div
      ref={menuRef}
      className="kpi-card-accounts-menu kpi-card-accounts-menu--portal"
      style={menuStyle}
      role="menu"
    >
      <div className="kpi-card-accounts-menu-header">
        <span className="kpi-card-accounts-menu-title">Saldos no extrato</span>
        <span className="kpi-card-accounts-menu-date">Atualizado hoje</span>
      </div>
      <ul className="kpi-card-accounts-list">
        {accountBalances.accounts.map((account) => (
          <li key={account.id}>
            <button
              type="button"
              className="kpi-card-accounts-item"
              role="menuitem"
              onClick={() => {
                setAccountsOpen(false)
                navigate(actionPath)
              }}
            >
              <span className="kpi-card-accounts-item-icon" aria-hidden>
                {account.accountType === 'digital' ? (
                  <CreditCard size={16} />
                ) : (
                  <Banknote size={16} />
                )}
              </span>
              <span className="kpi-card-accounts-item-info">
                <span className="kpi-card-accounts-item-name">{account.name}</span>
                <span className="kpi-card-accounts-item-type">
                  {ACCOUNT_TYPE_LABELS[account.accountType] || account.accountType}
                </span>
              </span>
              <span className={`kpi-card-accounts-item-balance${account.balance < 0 ? ' negative' : ''}`}>
                {formatCurrency(account.balance)}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <div className="kpi-card-accounts-total">
        <span>Total consolidado</span>
        <span className={accountBalances.total < 0 ? 'negative' : ''}>
          {formatCurrency(accountBalances.total)}
        </span>
      </div>
    </div>
  )

  const detailsMenu = detailsOpen && detailsMenuStyle && (
    <div
      ref={detailsMenuRef}
      className="kpi-card-details-menu kpi-card-accounts-menu--portal"
      style={detailsMenuStyle}
      role="dialog"
      aria-label={details?.title || 'Detalhes do indicador'}
    >
      <div className="kpi-card-accounts-menu-header">
        <span className="kpi-card-accounts-menu-title">{details?.title || label}</span>
        <span className="kpi-card-accounts-menu-date">Atualizado hoje</span>
      </div>
      <ul className="kpi-card-accounts-list">
        {detailItems.length === 0 && (
          <li className="kpi-card-details-empty">Nenhum título encontrado.</li>
        )}
        {detailItems.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              className="kpi-card-accounts-item"
              onClick={() => {
                setDetailsOpen(false)
                if (details?.actionPath) {
                  navigate(details.actionPath, {
                    state: {
                      dashboardPreset: details?.preset || null,
                      dueDateFrom: details?.dueDateFrom || null,
                      dueDateTo: details?.dueDateTo || null
                    }
                  })
                }
              }}
            >
              <span className="kpi-card-accounts-item-info">
                <span className="kpi-card-accounts-item-name">{item.description}</span>
                <span className="kpi-card-accounts-item-type">
                  {item.partyName || 'Sem vínculo'} - Venc.: {formatDueDate(item.dueDate)}
                </span>
              </span>
              <span className={`kpi-card-accounts-item-balance${item.amount < 0 ? ' negative' : ''}`}>
                {formatCurrency(item.amount)}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <div className="kpi-card-details-footer">
        <button
          type="button"
          className="kpi-card-details-action"
          onClick={() => {
            setDetailsOpen(false)
            if (details?.actionPath) {
              navigate(details.actionPath, {
                state: {
                  dashboardPreset: details?.preset || null,
                  dueDateFrom: details?.dueDateFrom || null,
                  dueDateTo: details?.dueDateTo || null
                }
              })
            }
          }}
        >
          {details?.actionLabel || 'Abrir listagem completa'}
        </button>
      </div>
    </div>
  )

  const handleToggleDetails = () => {
    setAccountsOpen(false)
    setDetailsOpen((open) => !open)
  }

  const handleCardClick = () => {
    if (details?.cardActionPath) {
      navigate(details.cardActionPath, {
        state: {
          preset: details?.cardActionPreset || details?.preset || null,
          dashboardPreset: details?.cardActionPreset || details?.preset || null,
          dateFrom: details?.dueDateFrom || null,
          dateTo: details?.dueDateTo || null,
          dueDateFrom: details?.dueDateFrom || null,
          dueDateTo: details?.dueDateTo || null
        }
      })
      return
    }
    handleToggleDetails()
  }

  return (
    <div
      ref={cardRef}
      className={cardClassName}
      data-onboarding={tourAnchor || undefined}
      role={showDetailsFlyout || onClick ? 'button' : undefined}
      tabIndex={showDetailsFlyout || onClick ? 0 : undefined}
      onClick={showDetailsFlyout ? handleCardClick : onClick}
      onKeyDown={showDetailsFlyout || onClick ? (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          if (showDetailsFlyout) handleCardClick()
          else onClick?.(event)
        }
      } : undefined}
    >
      {showAccountsFlyout && (
        <div className="kpi-card-accounts-flyout">
          <button
            ref={triggerRef}
            type="button"
            className={`kpi-card-accounts-trigger${accountsOpen ? ' is-open' : ''}`}
            onClick={(event) => {
              event.stopPropagation()
              setDetailsOpen(false)
              setAccountsOpen((open) => !open)
            }}
            aria-expanded={accountsOpen}
            aria-haspopup="true"
            title="Clique para ver saldos das contas"
          >
            <Landmark size={18} aria-hidden />
          </button>
        </div>
      )}

      {accountsMenu && createPortal(accountsMenu, document.body)}
      {detailsMenu && createPortal(detailsMenu, document.body)}

      <div className="kpi-card-header">
        <div className={`kpi-card-icon ${bg} ${color}`}>
          {Icon && <Icon size={24} strokeWidth={2} />}
        </div>
        <div className={`kpi-card-change ${up ? 'positive' : 'negative'}`}>
          {up ? '↑' : '↓'} {change}
        </div>
      </div>
      <p className="kpi-card-label">{label}</p>
      <p className="kpi-card-value">{value}</p>
    </div>
  )
}

export default KPICard
